// =============================================================================
// PPITriagem — triagem dos inscritos, por vaga
// =============================================================================
// Escolhe um processo → lista os candidatos agrupados por vaga, já com faltas e
// atestados APURADOS do espelho de ponto. Ações por linha: Aprovar / Reprovar.
// Botão de disparo dos avisos por WhatsApp (com opção de simular antes de valer).
//
// ⚠️ O AJUSTE MANUAL de faltas/atestados FOI REMOVIDO (2026-07-14, pedido da Julia).
// O número que aparece é o do espelho de ponto, e é o que vale — o RH decide olhando
// o dado real, sem poder sobrescrevê-lo. (O backend também parou de aceitar o ajuste;
// não é só a tela que esconde o campo.)

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    CheckCircle, XCircle, Send, FlaskConical, RefreshCcw,
    ChevronUp, ChevronDown, ChevronsUpDown,
} from 'lucide-react';
import { CORES, BadgeStatus, Botao, estilos } from './ppiUI';
// Desistência (2026-08-06): a mesma ação da LIVE, agora onde ela mais acontece —
// o candidato que some ENTRE a inscrição e a prova. Antes disto, para tirá-lo da
// lista era preciso REPROVÁ-LO na triagem, o que mente no histórico.
import {
    ehDesistente, ModalDesistencia, MarcaDesistiu, BotaoDesistir, BotaoDesfazerDesistencia,
} from './PPIDesistencia';
// A marca de quem foi incluído na prova por fora da triagem (2026-08-24).
import { MarcaIncluido } from './PPIInclusao';
import PPIEnvios from './PPIEnvios';

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

// ─────────────────────────────────────────────────────────────────────────────
// ORDENAÇÃO DAS TABELAS (2026-08-14)
// ─────────────────────────────────────────────────────────────────────────────
// A triagem de um processo grande é lida procurando gente: "quem tem mais faltas",
// "quem é da minha loja". Sem ordenar, isso é feito com o olho, linha a linha.
//
// ⚠️ É ordenação DE TELA: nada vai ao servidor, nada é gravado. A lista chega do
// backend em `ORDER BY NOME` e continua chegando assim — aqui só reordenamos o que
// já está na mão.
//
// ⚠️ E é POR VAGA: cada vaga desenha a sua tabela, então cada uma tem a sua ordem.
// Uma ordem global faria clicar em "Faltas" na vaga de Treinador reordenar também
// a de Coordenador, que o RH nem está olhando.
const COLUNAS = [
    { chave: 'NOME', rotulo: 'Candidato', tipo: 'texto' },
    { chave: 'LOJA_ATUAL', rotulo: 'Loja', tipo: 'texto' },
    { chave: 'CARGO_ATUAL', rotulo: 'Cargo atual', tipo: 'texto' },
    { chave: 'FALTAS_APURADAS', rotulo: 'Faltas', tipo: 'numero' },
    { chave: 'ATESTADOS_APURADOS', rotulo: 'Atestados', tipo: 'numero' },
    { chave: 'SITUACAO', rotulo: 'Situação', tipo: 'situacao' },
];

// A ordem inicial é a que o servidor já entrega — trocá-la faria a tela "pular"
// sozinha na primeira renderização, sem ninguém ter clicado em nada.
const ORDEM_PADRAO = { campo: 'NOME', dir: 'asc' };

// A Situação não é texto — ordená-la em ordem alfabética daria
// Aprovado › Desistiu › Pendente › Reprovado, que não quer dizer nada.
// A ordem é a do FUNIL, e o Pendente vem primeiro de propósito: é a fila de
// trabalho do RH, o que ele abriu a tela para resolver.
const rankSituacao = (c) => (
    ehDesistente(c.STATUS) ? 3
        : c.TRIAGEM_RESULTADO === 'Aprovado' ? 1
            : c.TRIAGEM_RESULTADO === 'Reprovado' ? 2
                : 0
);

// Comparador de uma coluna.
//
// ⚠️ VAZIO VAI SEMPRE PARA O FIM, nos DOIS sentidos — e isso vale tanto para o
// número quanto para o texto. O `null` de Faltas/Atestados significa "o espelho
// daquele período não foi importado" (é o "—" cinza do `CelulaNumero`), NÃO zero:
// deixá-lo subir no crescente colocaria "não sei" no lugar de "nenhuma falta", que
// é exatamente a mentira que a célula foi desenhada para evitar.
//
// ⚠️ Número compara como NÚMERO. Como texto, 10 viria antes de 2.
function compararPor(coluna, dir) {
    const sinal = dir === 'desc' ? -1 : 1;
    const vazio = (v) => v === null || v === undefined || v === '';
    return (a, b) => {
        if (coluna.tipo === 'situacao') return (rankSituacao(a) - rankSituacao(b)) * sinal;

        const va = a[coluna.chave];
        const vb = b[coluna.chave];
        if (vazio(va) && vazio(vb)) return 0;
        if (vazio(va)) return 1;      // sem sinal: o fim é o fim nos dois sentidos
        if (vazio(vb)) return -1;

        if (coluna.tipo === 'numero') return (Number(va) - Number(vb)) * sinal;
        return String(va).localeCompare(String(vb), 'pt-BR', { sensitivity: 'base' }) * sinal;
    };
}

// Ordena SEM mutar (a lista vem do estado — um `.sort()` direto a reordenaria no
// lugar e o React não veria mudança nenhuma).
// O empate cai no nome porque o `sort` do JS é ESTÁVEL e a lista já chega do
// servidor em ordem alfabética: duas pessoas com 3 faltas saem em ordem de nome,
// de graça.
function ordenarCandidatos(candidatos, ordem) {
    const coluna = COLUNAS.find((c) => c.chave === ordem.campo);
    if (!coluna) return candidatos;
    return [...candidatos].sort(compararPor(coluna, ordem.dir));
}

// ─────────────────────────────────────────────────────────────────────────────
// MARCA DE SAÍDA (2026-08-14) — quem está deixando a empresa
// ─────────────────────────────────────────────────────────────────────────────
// Quem tem marca de saída (aviso prévio, demissão, justa causa, abandono) não é
// mais convidado nem consegue se inscrever. Mas quem JÁ ESTAVA inscrito quando
// ficou inelegível **continua no processo** — a decisão da Julia foi sinalizar,
// não excluir: a pessoa se inscreveu de boa-fé quando podia, e quem decide o que
// fazer é o RH.
//
// ⚠️ SÃO DOIS AVISOS DIFERENTES, e juntá-los seria o erro:
//   • âmbar "está saindo"   → o espelho ACUSA um motivo de desligamento;
//   • cinza "verificar"     → o espelho NÃO TEM LANÇAMENTO no período; não dá
//                             para afirmar nem negar (9,4% das pessoas, medido).
// "Não achei" e "não sei" não podem virar o mesmo silêncio — é o mesmo princípio
// do "—" cinza de Faltas/Atestados, que existe para não mostrar 0 quando o
// espelho não foi importado.
//
// ⚠️ Âmbar, nunca vermelho: estar saindo não é reprovação. O vermelho é da
// decisão do RH, e emprestá-lo aqui misturaria os dois julgamentos — o mesmo
// cuidado que a desistência tomou ao ficar cinza.
function MarcaSaida({ estado, motivo, dias }) {
    if (estado === 'saida') {
        return (
            <span title={`Marca no espelho de ponto nos últimos ${dias || 7} dias`}>
                <BadgeStatus tom="alerta">{motivo || 'Está saindo'}</BadgeStatus>
            </span>
        );
    }
    if (estado === 'verificar') {
        return (
            <span title={`Sem nenhum lançamento no espelho nos últimos ${dias || 7} dias — não dá para saber se está saindo`}>
                <BadgeStatus tom="neutro">sem ponto no período — verificar</BadgeStatus>
            </span>
        );
    }
    return null;
}

// Cabeçalho clicável. Sem ordenação ativa mostra as DUAS setas (o convite: "dá
// para ordenar por aqui"); ativa, mostra só a seta do sentido, na cor do módulo.
function ThOrdenavel({ coluna, ordem, onClick }) {
    const ativa = ordem.campo === coluna.chave;
    const Icone = !ativa ? ChevronsUpDown : (ordem.dir === 'asc' ? ChevronUp : ChevronDown);
    return (
        <th
            style={{ ...estilos.th, cursor: 'pointer', userSelect: 'none' }}
            onClick={onClick}
            title={`Ordenar por ${coluna.rotulo}`}
        >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                {coluna.rotulo}
                <Icone size={13} style={{ color: ativa ? CORES.primaria : '#cbd5e1', flexShrink: 0 }} />
            </span>
        </th>
    );
}

function PPITriagem() {
    const [processos, setProcessos] = useState([]);
    const [idProcesso, setIdProcesso] = useState('');
    const [dados, setDados] = useState(null); // { processo, vagas:[{...vaga, candidatos}] }
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');
    const [msg, setMsg] = useState('');
    const [desistindo, setDesistindo] = useState(null);   // candidato no modal de desistência
    const [ordens, setOrdens] = useState({});   // { [idVaga]: {campo, dir} } — uma ordem POR VAGA
    const enviosRef = useRef(null);             // recarrega o card de Envios sob demanda

    // Clicar na coluna já ordenada inverte o sentido; clicar em outra começa
    // crescente (é o que se espera de uma coluna nova).
    const alternarOrdem = (idVaga, campo) => setOrdens((atuais) => {
        const atual = atuais[idVaga] || ORDEM_PADRAO;
        const dir = atual.campo === campo && atual.dir === 'asc' ? 'desc' : 'asc';
        return { ...atuais, [idVaga]: { campo, dir } };
    });

    // Desistências do PROCESSO INTEIRO. É aqui que esse número vive (2026-08-06,
    // decisão da Julia): a Triagem é a única tela que lista TODOS os inscritos —
    // na LIVE, o card mede a worklist dela, que só traz quem passou na prova.
    const totalDesistentes = useMemo(
        () => (dados?.vagas || []).reduce(
            (s, v) => s + v.candidatos.filter((c) => ehDesistente(c.STATUS)).length, 0),
        [dados]
    );

    // Quantos inscritos estão com marca de saída / sem ponto no período (2026-08-14).
    // O aviso só aparece quando há alguém — "0 saindo" num processo saudável é ruído.
    const marcasSaida = useMemo(() => {
        const todos = (dados?.vagas || []).flatMap((v) => v.candidatos);
        return {
            saindo: todos.filter((c) => c.SAIDA_ESTADO === 'saida').length,
            verificar: todos.filter((c) => c.SAIDA_ESTADO === 'verificar').length,
        };
    }, [dados]);

    // Lista de processos para o seletor.
    useEffect(() => {
        (async () => {
            try {
                const resp = await fetch('/api/treinamento/ppi/processos', { headers: authHeaders() });
                if (resp.ok) setProcessos(await resp.json());
            } catch (_) { /* silencioso */ }
        })();
    }, []);

    const carregarTriagem = useCallback(async (id) => {
        if (!id) { setDados(null); return; }
        setCarregando(true); setErro(''); setMsg('');
        try {
            const resp = await fetch(`/api/treinamento/ppi/processos/${id}/triagem`, { headers: authHeaders() });
            if (!resp.ok) throw new Error('Erro ao carregar a triagem.');
            setDados(await resp.json());
        } catch (e) { setErro(e.message); }
        finally { setCarregando(false); }
    }, []);

    const onSelecionar = (e) => {
        const id = e.target.value;
        setIdProcesso(id);
        carregarTriagem(id);
    };

    const decidir = async (cand, resultado) => {
        try {
            const resp = await fetch(`/api/treinamento/ppi/candidato/${cand.ID}/triagem`, {
                method: 'PATCH', headers: authHeaders(),
                body: JSON.stringify({ resultado }),
            });
            if (!resp.ok) throw new Error('Erro ao registrar a decisão.');
            carregarTriagem(idProcesso);
        } catch (e) { setErro(e.message); }
    };

    // Disparo dos avisos da triagem. Não envia na hora: ENFILEIRA e o worker manda
    // aos poucos (com freio e retry). Quem não receber aparece no card de Envios,
    // com o motivo, e dá para reenviar de lá.
    const dispararAvisos = async (dryRun) => {
        const rotulo = dryRun
            ? 'ver a PRÉVIA (nada é enfileirado nem enviado)'
            : 'ENFILEIRAR os avisos por WhatsApp';
        if (!window.confirm(`Confirma ${rotulo} para todos os candidatos já triados?`)) return;
        setMsg(''); setErro('');
        try {
            const resp = await fetch(`/api/treinamento/ppi/processos/${idProcesso}/triagem/disparar`, {
                method: 'POST', headers: authHeaders(), body: JSON.stringify({ dryRun }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.message || 'Erro no disparo.');
            // Telefone ausente/inválido não vai ao ManyChat — entra como "Não enviado"
            // no card de Envios, com o motivo, e de lá dá para corrigir e reenviar.
            const semTel = data.telefoneRuim
                ? ` · ${data.telefoneRuim} com telefone inválido ou ausente (não recebem — veja em Envios)`
                : '';
            setMsg(`${data.message} ${data.aprovados} aprovado(s) · ${data.reprovados} reprovado(s)${semTel}.`);
            if (!dryRun) setTimeout(() => enviosRef.current?.(), 1200);   // puxa o placar
        } catch (e) { setErro(e.message); }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <div style={{ minWidth: '280px' }}>
                    <label style={estilos.rotuloFiltro}>Processo</label>
                    <select style={estilos.input} value={idProcesso} onChange={onSelecionar}>
                        <option value="">Selecione um processo…</option>
                        {processos.map((p) => (
                            <option key={p.ID} value={p.ID}>{p.NOME_PROCESSO} — {p.STATUS} ({p.QTD_CANDIDATOS} inscritos)</option>
                        ))}
                    </select>
                </div>
                {dados && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Botao variant="secundario" icone={RefreshCcw} onClick={() => carregarTriagem(idProcesso)}>Atualizar</Botao>
                        <Botao variant="secundario" icone={FlaskConical} onClick={() => dispararAvisos(true)}>Prévia</Botao>
                        <Botao icone={Send} onClick={() => dispararAvisos(false)}>Disparar avisos</Botao>
                    </div>
                )}
            </div>

            {erro && <p style={{ color: '#be123c' }}>{erro}</p>}
            {msg && <div style={{ backgroundColor: '#ecfdf5', color: '#047857', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>{msg}</div>}
            {carregando && <p style={{ color: CORES.textoFraco }}>Carregando…</p>}

            {/* A fila do disparo da triagem — quem recebeu, quem falhou (e por quê), reenvio. */}
            {idProcesso && (
                <PPIEnvios idProcesso={idProcesso} grupo="triagem"
                    titulo="Envios da triagem (convocação / não selecionado)" recarregarRef={enviosRef} />
            )}

            {/* O total do processo, quando existe. Só aparece com alguém — "0 desistiram"
                num processo que nem começou é ruído; a informação aqui é a exceção. */}
            {dados && totalDesistentes > 0 && (
                <p style={{ fontSize: '13px', color: CORES.textoSec, margin: '0 0 16px 0' }}>
                    <strong>{totalDesistentes}</strong> candidato(s) desistiram deste processo — eles seguem
                    na lista, marcados, e ficam fora da triagem e da chamada da sala.
                </p>
            )}

            {/* Explicação das marcas de saída. Aparece só quando há alguém marcado —
                e diz o essencial: NINGUÉM foi removido. Sem esta frase, a marca
                pareceria uma decisão já tomada pelo sistema. */}
            {dados && (marcasSaida.saindo > 0 || marcasSaida.verificar > 0) && (
                <div style={{
                    backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px',
                    padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#92400e',
                }}>
                    {marcasSaida.saindo > 0 && (
                        <div>
                            <strong>{marcasSaida.saindo}</strong> inscrito(s) com marca de <strong>saída</strong> no
                            espelho de ponto dos últimos {dados.diasJanelaSaida || 7} dias (aviso prévio, demissão,
                            justa causa ou abandono). Eles <strong>continuam no processo</strong> — inscreveram-se
                            quando ainda eram elegíveis, e a decisão é sua.
                        </div>
                    )}
                    {marcasSaida.verificar > 0 && (
                        <div style={{ marginTop: marcasSaida.saindo > 0 ? '6px' : 0 }}>
                            <strong>{marcasSaida.verificar}</strong> sem <strong>nenhum lançamento</strong> no espelho
                            no período — não dá para afirmar nem negar que estão saindo. Vale conferir com a loja.
                        </div>
                    )}
                </div>
            )}

            {dados && dados.vagas.map((v) => {
                const desistentesDaVaga = v.candidatos.filter((c) => ehDesistente(c.STATUS)).length;
                const ordem = ordens[v.ID] || ORDEM_PADRAO;
                const candidatosOrdenados = ordenarCandidatos(v.candidatos, ordem);
                return (
                <div key={v.ID} style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <h4 style={{ fontSize: '17px', fontWeight: 800, color: CORES.texto, margin: 0 }}>{v.CARGO}</h4>
                        <BadgeStatus tom="neutro">{v.candidatos.length} inscrito(s)</BadgeStatus>
                        {desistentesDaVaga > 0 && (
                            <BadgeStatus tom="neutro">{desistentesDaVaga} desistiram</BadgeStatus>
                        )}
                        {v.DATA_AVALIACAO && (
                            <span style={{ fontSize: '13px', color: CORES.textoFraco }}>
                                Avaliação: {new Date(v.DATA_AVALIACAO).toLocaleDateString('pt-BR')}
                                {v.HORA_AVALIACAO ? ` às ${String(v.HORA_AVALIACAO).slice(0, 5)}` : ''}
                                {v.LOCAL_AVALIACAO ? ` — ${v.LOCAL_AVALIACAO}` : ''}
                            </span>
                        )}
                    </div>

                    <div style={estilos.tabelaWrap}>
                        <table style={estilos.tabela}>
                            <thead><tr>
                                {COLUNAS.map((col) => (
                                    <ThOrdenavel key={col.chave} coluna={col} ordem={ordem}
                                        onClick={() => alternarOrdem(v.ID, col.chave)} />
                                ))}
                                {/* Decisão não ordena: é a coluna dos BOTÕES, não um dado. */}
                                <th style={{ ...estilos.th, textAlign: 'center' }}>Decisão</th>
                            </tr></thead>
                            <tbody>
                                {candidatosOrdenados.map((c) => (
                                    <tr key={c.ID}>
                                        {/* A marca de saída fica sob o NOME, não na coluna Situação:
                                            ela fala da situação da pessoa na EMPRESA, e a Situação
                                            fala da decisão do RH no processo. Empilhar as duas no
                                            mesmo lugar faria uma parecer consequência da outra. */}
                                        <td style={estilos.td}>
                                            <strong>{c.NOME}</strong>
                                            {c.SAIDA_ESTADO && (
                                                <div style={{ marginTop: '4px' }}>
                                                    <MarcaSaida estado={c.SAIDA_ESTADO} motivo={c.SAIDA_MOTIVO}
                                                        dias={dados?.diasJanelaSaida} />
                                                </div>
                                            )}
                                        </td>
                                        <td style={estilos.td}>{c.LOJA_ATUAL || '—'}</td>
                                        <td style={estilos.td}>{c.CARGO_ATUAL || '—'}</td>
                                        <td style={estilos.td}><CelulaNumero apurado={c.FALTAS_APURADAS} /></td>
                                        <td style={estilos.td}><CelulaNumero apurado={c.ATESTADOS_APURADOS} /></td>
                                        <td style={estilos.td}>
                                            {/* A desistência vem PRIMEIRO: ela substitui o estado da triagem.
                                                Mostrar "Aprovado" de quem avisou que saiu mandaria o RH
                                                convocá-lo para a prova. */}
                                            {ehDesistente(c.STATUS)
                                                ? <MarcaDesistiu motivo={c.DESISTENCIA_MOTIVO} largura="220px" />
                                                /* ⚠️ INCLUÍDO DEPOIS vem ANTES da leitura da triagem
                                                   (2026-08-24). A inclusão grava 'Aprovado' no campo da
                                                   triagem para os portões da prova funcionarem — sem esta
                                                   linha, esta lista afirmaria que a TRIAGEM aprovou quem
                                                   ela nunca avaliou, e pior: quem ela REPROVOU. */
                                                : c.INCLUSAO_POSTERIOR === 1
                                                    ? <MarcaIncluido inclusao={{
                                                        motivo: c.INCLUSAO_MOTIVO,
                                                        por: c.INCLUSAO_USUARIO_NOME,
                                                        foraDoRecorte: c.INCLUSAO_FORA_RECORTE === 1,
                                                        triagemAnterior: c.INCLUSAO_TRIAGEM_ANTERIOR || null,
                                                    }} />
                                                    : <>
                                                        {c.TRIAGEM_RESULTADO === 'Aprovado' && <BadgeStatus tom="aprovado">Aprovado</BadgeStatus>}
                                                        {c.TRIAGEM_RESULTADO === 'Reprovado' && <BadgeStatus tom="reprovado">Reprovado</BadgeStatus>}
                                                        {!c.TRIAGEM_RESULTADO && <BadgeStatus tom="rascunho">Pendente</BadgeStatus>}
                                                    </>}
                                        </td>
                                        <td style={{ ...estilos.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            {/* Desistente: a única ação é desfazer — o servidor recusa
                                                triá-lo (409), porque a decisão sobrescreveria o STATUS
                                                e apagaria a desistência sem ninguém ver. */}
                                            {ehDesistente(c.STATUS) ? (
                                                <BotaoDesfazerDesistencia
                                                    idCandidato={c.ID} nome={c.NOME} compacto
                                                    onPronto={() => carregarTriagem(idProcesso)} onErro={setErro} />
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                                                    <button onClick={() => decidir(c, 'Aprovado')} title="Aprovar" style={btnAprovar}><CheckCircle size={18} /></button>
                                                    <button onClick={() => decidir(c, 'Reprovado')} title="Reprovar" style={btnReprovar}><XCircle size={18} /></button>
                                                    <BotaoDesistir onClick={() => setDesistindo({ ...c, VAGA: v.CARGO })} />
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {!v.candidatos.length && <tr><td style={estilos.td} colSpan={7}>Nenhum inscrito nesta vaga.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
                );
            })}

            {dados && !dados.vagas.length && <p style={{ color: CORES.textoFraco }}>Este processo não tem vagas.</p>}

            {desistindo && (
                <ModalDesistencia
                    idCandidato={desistindo.ID} nome={desistindo.NOME} detalhe={desistindo.VAGA}
                    onFechar={() => setDesistindo(null)}
                    onPronto={() => { setDesistindo(null); carregarTriagem(idProcesso); }} />
            )}
        </div>
    );
}

// Célula de número: o valor APURADO do espelho de ponto — só leitura.
// O "—" cinza é honesto: significa "o espelho daquele período ainda não foi
// importado", e é diferente de "zero faltas". Mostrar 0 nesse caso seria mentir.
function CelulaNumero({ apurado }) {
    return (
        <span style={{
            fontWeight: 700,
            color: apurado === null ? '#94a3b8' : CORES.texto,
            fontVariantNumeric: 'tabular-nums',
        }}>
            {apurado === null ? '—' : apurado}
        </span>
    );
}

const btnAcaoBase = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '7px', borderRadius: '8px', backgroundColor: '#fff', cursor: 'pointer', margin: '0 3px' };
const btnAprovar = { ...btnAcaoBase, border: '1px solid #a7f3d0', color: '#059669' };
const btnReprovar = { ...btnAcaoBase, border: '1px solid #fbb6c4', color: '#e11d48' };

export default PPITriagem;
